import { useEffect, useRef, useState } from "react"
import { useLocation, useNavigate, useParams } from "react-router-dom"
import "../../main.scss"
import { resolveApiBaseUrl } from "../api/apiBase"
import "./User.scss"
import { authAPI, isDemoAccountEmail } from "../api/authApi"
import { formatAuthRateLimitMessage } from "../api/apiError"
import { useAuth } from "../auth/AuthContext"
import Logo from "@/components/Logo"
import GetInTouchPanel from "@/components/GetInTouchPanel"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { useAppVersion } from "@/hooks/useAppVersion"

type TenantSummary = {
  uid: string;
  name: string;
  abbreviation: string;
  description?: string;
  imageUrl?: string;
};

const Login = ({
  defaultEmail = "",
}: {
  defaultEmail?: string
}) => {
  const e = import.meta.env
  const location = useLocation()
  const { tenantAbbreviation: tenantAbbreviationFromParams } = useParams<{ tenantAbbreviation?: string }>()
  const stateEmail = (location.state as { email?: string } | null)?.email ?? ""

  const [email, setEmail] = useState(defaultEmail || stateEmail || "")
  const [password, setPassword] = useState("")
  const [error, setError] = useState("")
  const [message, setMessage] = useState("")
  const [busy, setBusy] = useState(false)
  const [formLocked, setFormLocked] = useState(false)
  const { version: appVersion, releaseDate: appReleaseDate } = useAppVersion()
  const [labwickAppEnvLabel, setLabwickAppEnvLabel] = useState(
    typeof e.VITE_LABWICK_APP_ENV === "string" && e.VITE_LABWICK_APP_ENV.trim()
      ? e.VITE_LABWICK_APP_ENV.trim()
      : ""
  )
  const [rateLimitUntil, setRateLimitUntil] = useState<number | null>(null)
  const [remainingSeconds, setRemainingSeconds] = useState(0)
  const [showWaitMessage, setShowWaitMessage] = useState(false)
  const [contactDialogOpen, setContactDialogOpen] = useState(false)
  const [selectedTenant, setSelectedTenant] = useState<TenantSummary | null>(null)
  const navigate = useNavigate()
  const { refreshUser } = useAuth()
  const containerRef = useRef<HTMLDivElement | null>(null)
  const isDemoLogin = isDemoAccountEmail(email.trim())

  useEffect(() => {
    const next = defaultEmail || stateEmail
    if (next && !email) {
      setEmail(next)
    }
  }, [defaultEmail, stateEmail, email])

  useEffect(() => {
    const baseUrl = resolveApiBaseUrl()
    fetch(`${baseUrl}/health/version`, { cache: "no-store" })
      .then((response) => (response.ok ? response.json() : null))
      .then((data: { labwickAppEnv?: string | null } | null) => {
        if (!data || typeof data !== "object" || !("labwickAppEnv" in data)) {
          return
        }
        const raw = data.labwickAppEnv
        if (typeof raw === "string" && raw.trim()) {
          setLabwickAppEnvLabel(raw.trim())
        }
      })
      .catch(() => { })
  }, [])

  useEffect(() => {
    if (error && containerRef.current) {
      containerRef.current.scrollIntoView({ behavior: "smooth", block: "start" })
    }
  }, [error])

  useEffect(() => {
    if (rateLimitUntil == null) return
    const tick = () => {
      const secs = Math.max(0, Math.ceil((rateLimitUntil - Date.now()) / 1000))
      setRemainingSeconds(secs)
      if (secs <= 0) {
        setRateLimitUntil(null)
        setShowWaitMessage(false)
      }
    }
    tick()
    const id = setInterval(tick, 1000)
    return () => clearInterval(id)
  }, [rateLimitUntil])

  useEffect(() => {
    const abbr = tenantAbbreviationFromParams?.trim() || ""
    if (!abbr) {
      return
    }
    let cancelled = false
    const fetchTenant = async () => {
      try {
        const baseUrl = resolveApiBaseUrl()
        const response = await fetch(`${baseUrl}/tenants/by-abbreviation/${encodeURIComponent(abbr)}`)
        if (!response.ok) {
          if (!cancelled) setSelectedTenant(null)
          return
        }
        const data = await response.json().catch(() => null)
        if (!data?.success || !data.tenant || cancelled) {
          return
        }
        setSelectedTenant(data.tenant as TenantSummary)
      } catch {
        if (!cancelled) setSelectedTenant(null)
      }
    }
    fetchTenant()
    return () => {
      cancelled = true
    }
  }, [tenantAbbreviationFromParams])

  const isValidEmail = (value: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)

  const handleLogin = async () => {
    if (formLocked || busy) {
      return
    }
    setBusy(true)
    setError("")
    setMessage("")
    try {
      const normalizedEmail = email.trim().toLowerCase()
      if (!normalizedEmail) {
        setError("Email is required")
        return
      }

      if (!isValidEmail(normalizedEmail)) {
        setError("Please enter a valid email address")
        return
      }

      if (isDemoAccountEmail(normalizedEmail)) {
        if (!password.trim()) {
          setError("Password is required")
          return
        }
        await authAPI.loginDemo(normalizedEmail, password, {
          tenantAbbreviation: selectedTenant?.abbreviation,
        })
        await refreshUser()
        navigate("/")
        return
      }

      const result = await authAPI.loginInitiate(normalizedEmail, {
        tenantAbbreviation: selectedTenant?.abbreviation,
      })
      setEmail(normalizedEmail)
      setMessage(result.message || "Check your email for a login link. It will expire in 10 minutes.")
      setFormLocked(true)
    } catch (e: any) {
      if (e?.status === 404) {
        setError("No account found with this email address.")
      } else if (e?.status === 400) {
        setError("Please enter a valid email address.")
      } else if (e?.status === 429) {
        const secs = (e as any)?.retryAfterSeconds ?? 60
        setRateLimitUntil(Date.now() + secs * 1000)
        setError(formatAuthRateLimitMessage(secs))
      } else {
        const errorMessage = e.message || "Failed to send login link. Please try again."
        setError(errorMessage)
      }
    } finally {
      setBusy(false)
    }
  }

  const navRegister = () => {
    const path = selectedTenant?.abbreviation
      ? `/register/${encodeURIComponent(selectedTenant.abbreviation)}`
      : "/register"
    navigate(path, { state: { email: email.trim() || undefined } })
  }

  const keyPress = (event: any) => {
    if (event.key === "Enter") {
      handleLogin()
    }
  }

  return (
    <div className="auth-page" ref={containerRef}>
      <div className="lw-home">
        <header className="lw-nav">
          <Logo size="md" withText />
        </header>

        <main className="lw-hero">
          <div className="lw-hero-copy">
            <span className="lw-hero-eyebrow">Labwick.com</span>
            <h1 className="lw-hero-title text-blue-400">Tailor-made collection management for your team.</h1>
            <p className="lw-hero-subtitle">
              Labwick helps operations and research teams design flexible collections, capture richer data,
              and connect public forms directly to the workflows that actually run the business.
            </p>
            <div className="lw-contact-card auth-card panel">
              <p className="lw-contact-text">
                Interested in using Labwick for your event, organisation, team or lab? Leave your details and we will get in touch soon.
              </p>
              <Button
                className="lw-contact-btn"
                onClick={() => setContactDialogOpen(true)}
              >
                Get in touch
              </Button>
            </div>
          </div>

          <div className="lw-hero-card">
            <div className="auth-card panel">
              <div className="auth-header">
                <h1>Log in to Labwick</h1>
                <p className="auth-subtitle">
                  {isDemoLogin
                    ? "Enter the demo account password to sign in."
                    : "Enter your email address to receive a passwordless login link."}
                </p>
              </div>
              {error && <p className="auth-alert auth-alert--error">{error}</p>}
              {message && <p className="auth-alert auth-alert--success">{message}</p>}

              <input
                id="login-email"
                type="email"
                placeholder="you@company.com"
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value)
                  if (error) setError("")
                }}
                disabled={formLocked || busy || ((defaultEmail?.length ?? 0) > 0)}
                className="textInput auth-input !bg-white border !border-blue-500"
                autoComplete="email"
                onKeyPress={keyPress}
              />

              {isDemoLogin ? (
                <div className="mt-3 space-y-2">
                  <Label htmlFor="login-password">Demo password</Label>
                  <Input
                    id="login-password"
                    type="password"
                    placeholder="Enter demo password"
                    value={password}
                    onChange={(event) => {
                      setPassword(event.target.value)
                      if (error) setError("")
                    }}
                    disabled={busy}
                    autoComplete="current-password"
                    onKeyPress={keyPress}
                    className="auth-input !bg-white border !border-blue-500"
                  />
                </div>
              ) : null}

              <button
                type="button"
                className="buttonSubmit auth-button"
                onClick={() => {
                  if (rateLimitUntil != null && Date.now() < rateLimitUntil) {
                    setShowWaitMessage(true)
                    return
                  }
                  handleLogin()
                }}
                disabled={
                  busy ||
                  formLocked ||
                  !isValidEmail(email.trim()) ||
                  (isDemoLogin && !password.trim())
                }
                style={remainingSeconds > 0 ? { cursor: 'not-allowed', opacity: 0.7 } : undefined}
              >
                {isDemoLogin
                  ? busy
                    ? "Signing in..."
                    : "Sign in"
                  : formLocked
                    ? "Link Sent"
                    : busy
                      ? "Sending..."
                      : remainingSeconds > 0
                        ? "Wait"
                        : "Send login link"}
              </button>
              {showWaitMessage && remainingSeconds > 0 && (
                <p className="auth-alert auth-alert--error">
                  Please wait {remainingSeconds} seconds before trying again.
                </p>
              )}
              {formLocked && (
                <p className="auth-help">
                  Check your email and follow the login link. We disabled this form until you finish verifying the
                  token.
                </p>
              )}
              <button
                type="button"
                className="auth-footer auth-footer--boxed auth-footer--stack auth-footer--cta"
                onClick={navRegister}
              >
                <span>Need an account?</span>
                <span className="auth-footer__link">Request access</span>
              </button>
            </div>
          </div>
        </main>

        <Dialog open={contactDialogOpen} onOpenChange={setContactDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Get in touch</DialogTitle>
              <DialogDescription>
                Leave your email and we will get in touch soon.
              </DialogDescription>
            </DialogHeader>
            <GetInTouchPanel
              source="labwick-login"
              onClose={() => setContactDialogOpen(false)}
            />
          </DialogContent>
        </Dialog>

        <div className="fixed bottom-2 right-4 text-[11px] text-gray-400 pointer-events-none select-none">
          <span>
            {[
              appVersion
                ? `v ${appVersion}${appReleaseDate ? ` · ${appReleaseDate}` : ""}`
                : "",
              labwickAppEnvLabel || "",
            ]
              .filter(Boolean)
              .join(" · ")}
          </span>
        </div>
      </div>
    </div>
  )
}

export default Login
